// Sofía Recepcionista flagship landing — castellano-only.
// Mounted by app.jsx router at `/sofia-demo`.
//
// Phase 2 lock interpretation: this is a NEW page (additive). The CLAUDE.md
// "Conventions" lock applies to landing copy/UX/pricing on the main `/` route,
// not to additive sibling pages.
//
// Cal.com embed: follows the iframe pattern from diagnostico.jsx (NOT the
// data-cal-link script-tag pattern). The literal `{{CAL_SLUG}}` placeholder
// must be swapped by the operator after creating the dedicated 30-min event
// in the sborzone workspace (suggested slug: sborzone/sofia-demo).
//
// Time-saved framing is h/mes throughout (per memory feedback_hours_per_month).
// Pricing canonical: €2.900 setup + €290/mes sin IVA · Kit Consulting €6.000
// Segmento A. AI Act Art. 50 disclosure surfaced in compliance microcopy.

import React, { useEffect, useState } from 'react';
import { navigateTo } from './shared.js';

/* ---------- COPY (inlined; this is a one-off page per locked decision D-01) ---------- */

const COPY = {
  nav: {
    wordmark: 'EnAutomático',
    cta: 'Reserva una llamada de 20 min'
  },
  hero: {
    eyebrow: 'Sofía Recepcionista 24/7 · enautomatico.pro/sofia-demo',
    h1a: 'Cero llamadas perdidas.',
    h1b: 'Sin contratar a nadie.',
    sub: 'Sofía atiende todas las llamadas de tu negocio en castellano, las 24 horas. Cualifica al interlocutor, agenda directamente en tu calendario y avisa a tu equipo. Habla como una recepcionista profesional, no como un robot.',
    cta: 'Reserva una llamada de 20 min',
    ctaSecondary: 'Ver presupuesto cerrado',
    stats: [
      { label: 'Implantación', value: '2 semanas' },
      { label: 'Cobertura', value: '24/7' },
      { label: 'Llamadas perdidas', value: '0' },
      { label: 'Tiempo liberado', value: '~60 h/mes' }
    ]
  },
  problema: {
    eyebrow: '§ 02 · El problema',
    h2: 'Cada llamada perdida es un cliente que llamó a la competencia.',
    cards: [
      {
        n: '15–25',
        unit: 'llamadas/sem perdidas',
        body: 'en una clínica dental media · 80–600 € de tratamiento por llamada que se va a la competencia.'
      },
      {
        n: '10–20',
        unit: 'llamadas/sem perdidas',
        body: 'en un taller mecánico mientras los mecánicos están con coches y nadie hay en oficina.'
      },
      {
        n: '20–40',
        unit: 'llamadas/sem perdidas',
        body: 'en una inmobiliaria cuando los comerciales están en visitas — y la competencia descuelga primero.'
      }
    ]
  },
  solucion: {
    eyebrow: '§ 03 · Cómo funciona Sofía',
    h2: 'Una recepcionista profesional que no duerme, no se distrae y agenda en tu calendario.',
    steps: [
      {
        n: '01',
        title: 'Atiende',
        body: 'Suena el teléfono, Sofía descuelga al primer tono. Saluda en castellano natural, identifica al interlocutor y entiende el motivo de la llamada.'
      },
      {
        n: '02',
        title: 'Cualifica',
        body: 'Pregunta lo justo: nombre, motivo, urgencia. Si es un caso para humano (queja, urgencia médica, presupuesto complejo), te lo deriva al instante con contexto.'
      },
      {
        n: '03',
        title: 'Agenda y registra',
        body: 'Si encaja, agenda directamente en Cal.com, Doctoralia, Klinikare o tu calendario actual. Cada llamada se transcribe y se archiva en tu CRM (Clientify, HubSpot, Notion, A3 vía Make).'
      }
    ],
    stack: ['Retell.ai', 'Telnyx Spain', 'ElevenLabs', 'Make.com', 'Cal.com', 'Telegram alerts']
  },
  roi: {
    eyebrow: '§ 04 · La economía',
    h2: 'Recuperas ~60 h/mes y dejas de perder llamadas. La inversión se paga sola.',
    leftTitle: 'Lo que recuperas',
    leftItems: [
      '~60 h/mes liberadas del teléfono',
      '€1.800/mes equivalente a €30/h equipo medio',
      'Trazabilidad completa de cada llamada en CRM',
      '0 llamadas perdidas en horario y fuera de horario'
    ],
    rightTitle: 'Lo que pagas',
    rightItems: [
      '€2.900 setup cerrado · 50% firma / 50% entrega · sin IVA',
      '€290/mes servicio gestionado · sin IVA · hasta 1.000 min/mes incluidos · soporte L1 <4h laborables',
      'DID + consumo a coste · €1/mes + €0,02/min facturados a coste'
    ],
    summary: 'ROI mensual neto ≈ €1.510/mes (≈ €18.120/año) una vez en marcha.',
    summaryCalc: '€1.800 valor del tiempo liberado − €290 servicio gestionado',
    kitTitle: 'Kit Consulting Segmento A',
    kitBody: 'Si tu empresa tiene ≤9 empleados, los €6.000 subvencionados cubren el setup completo + ~12 meses de servicio gestionado. Enautomático es Agente Digitalizador adherido y te acompaña en la solicitud sin coste adicional.',
    kitFootnote: '* Sujeto a acreditación vigente como Agente Digitalizador en el momento de la solicitud.'
  },
  reserva: {
    eyebrow: '§ 05 · Reserva una llamada de 20 min',
    h2: 'Hablamos 20 minutos. Ves si tiene sentido para tu negocio.',
    sub: 'Sin compromiso, sin demo prefabricada. Te explicamos el alcance del presupuesto cerrado y resolvemos dudas de implantación, integración y subvención. Si encaja, te enviamos el presupuesto firmable en 24 horas.'
  },
  faq: {
    eyebrow: '§ 06 · Preguntas frecuentes',
    items: [
      {
        q: '¿Qué pasa si Sofía no entiende al cliente?',
        a: 'Sofía está entrenada para identificar el límite de su competencia y derivar a una persona de tu equipo en cuanto detecta urgencia, queja o caso complejo. Tú decides el umbral en la fase de diseño (semana 1).'
      },
      {
        q: '¿Mi CRM actual se integra?',
        a: 'Sí en la mayoría de casos: Clientify, HubSpot, Notion, A3, Sage Despachos, Doctoralia, Klinikare, Sirius, calendarios propios. Si tu sistema no aparece, lo evaluamos en la llamada de 20 minutos y lo decimos honestamente.'
      },
      {
        q: '¿Cumple con el RGPD y la AI Act europea?',
        a: 'Sí. Firmamos contrato de encargado de tratamiento (DPA) al inicio. Sofía declara explícitamente al inicio de cada llamada que es un agente de IA, conforme al Artículo 50 del Reglamento Europeo de IA. Subprocesadores con SCC vigentes (Retell.ai, Telnyx, ElevenLabs, Anthropic, Make.com).'
      },
      {
        q: '¿Y si quiero cancelar?',
        a: 'Servicio gestionado mensual sin permanencia. Avisas con 30 días, conservas tus transcripciones y CRM, devolvemos el DID. El setup es un pago único no reembolsable una vez entregado en producción.'
      }
    ]
  },
  footer: {
    tagline: 'Sofía Recepcionista 24/7 · castellano · Barcelona',
    compliance: 'Sofía declara su naturaleza de IA al inicio de cada llamada (AI Act · Art. 50). Subprocesadores con SCC vigentes. DPA disponible bajo petición.',
    copyright: '© 2026 EnAutomático · enautomatico.pro · Barcelona'
  }
};

/* ---------- INLINE STYLES (component-scoped, follow legal.jsx cadence) ---------- */

const STYLES = `
  .sofia-page { background: var(--bg); color: var(--ink); font-family: var(--sans); min-height: 100vh; }
  .sofia-page section { padding: 88px 0; border-top: 1px solid var(--rule); }
  .sofia-page section:first-of-type { border-top: none; }
  .sofia-page h1 {
    font-family: var(--serif);
    font-weight: 400;
    letter-spacing: -0.02em;
    font-size: clamp(40px, 8vw, 88px);
    line-height: 0.95;
    margin: 0 0 24px;
  }
  .sofia-page h1 .h1b { font-style: italic; display: block; color: var(--ink-2); }
  .sofia-page h2 {
    font-family: var(--serif);
    font-weight: 400;
    letter-spacing: -0.015em;
    font-size: clamp(28px, 5vw, 56px);
    line-height: 1.0;
    margin: 0 0 20px;
  }
  .sofia-page p { line-height: 1.6; color: var(--ink-2); }
  .sofia-hero-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 64px;
    align-items: center;
  }
  .sofia-hero-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    border: 1px solid var(--rule);
    background: var(--paper);
  }
  .sofia-hero-stats > div {
    padding: 22px 24px;
    border-right: 1px solid var(--rule);
    border-bottom: 1px solid var(--rule);
  }
  .sofia-hero-stats > div:nth-child(2n) { border-right: none; }
  .sofia-hero-stats > div:nth-child(n+3) { border-bottom: none; }
  .sofia-hero-stats .label {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 8px;
  }
  .sofia-hero-stats .value {
    font-family: var(--serif);
    font-size: 28px;
    line-height: 1.1;
    color: var(--ink);
  }
  .sofia-cta-row { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 16px; }
  .sofia-three-col {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
  }
  .sofia-stat-card {
    padding: 32px 28px;
    border: 1px solid var(--rule);
    background: var(--paper);
  }
  .sofia-stat-card .num {
    font-family: var(--serif);
    font-size: 56px;
    line-height: 1;
    color: var(--ink);
    margin-bottom: 6px;
  }
  .sofia-stat-card .unit {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 18px;
  }
  .sofia-stat-card .body { font-size: 14px; color: var(--ink-2); line-height: 1.55; }
  .sofia-step-row {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 24px;
    padding: 28px 0;
    border-top: 1px solid var(--rule);
  }
  .sofia-step-row:last-of-type { border-bottom: 1px solid var(--rule); }
  .sofia-step-row .n {
    font-family: var(--mono);
    font-size: 13px;
    color: var(--ink-3);
    letter-spacing: 0.1em;
  }
  .sofia-step-row .title {
    font-family: var(--serif);
    font-size: 26px;
    margin-bottom: 8px;
    color: var(--ink);
  }
  .sofia-step-row .body { font-size: 16px; color: var(--ink-2); line-height: 1.6; max-width: 720px; }
  .sofia-stack-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 32px;
  }
  .sofia-roi-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    margin-top: 12px;
  }
  .sofia-roi-col { padding: 28px; border: 1px solid var(--rule); background: var(--paper); }
  .sofia-roi-col h3 {
    font-family: var(--mono);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--ink-3);
    margin: 0 0 18px;
  }
  .sofia-roi-col ul { list-style: none; padding: 0; margin: 0; }
  .sofia-roi-col li {
    font-size: 15px;
    color: var(--ink);
    line-height: 1.55;
    padding: 12px 0;
    border-top: 1px solid var(--rule);
  }
  .sofia-roi-col li:first-child { border-top: none; padding-top: 0; }
  .sofia-roi-summary {
    margin-top: 28px;
    padding: 24px 28px;
    background: var(--ink);
    color: var(--bg);
    border: 1px solid var(--ink);
  }
  .sofia-roi-summary .big {
    font-family: var(--serif);
    font-size: clamp(22px, 3.4vw, 32px);
    line-height: 1.2;
    color: var(--bg);
  }
  .sofia-roi-summary .calc {
    font-family: var(--mono);
    font-size: 12px;
    color: oklch(0.85 0.005 75);
    margin-top: 10px;
    letter-spacing: 0.04em;
  }
  .sofia-kit-card {
    margin-top: 28px;
    padding: 28px 32px;
    border: 1.5px solid var(--on);
    background: oklch(0.96 0.04 145 / 0.4);
  }
  .sofia-kit-card .kit-title {
    font-family: var(--serif);
    font-size: 22px;
    color: var(--on-ink);
    margin-bottom: 8px;
  }
  .sofia-kit-card .kit-body { font-size: 15px; color: var(--ink); line-height: 1.6; }
  .sofia-kit-card .kit-footnote {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-3);
    margin-top: 12px;
    line-height: 1.5;
  }
  .sofia-cal-wrapper {
    margin-top: 28px;
    border: 1px solid var(--rule);
    background: var(--paper);
    overflow: hidden;
  }
  .sofia-cal-iframe {
    width: 100%;
    height: 600px;
    border: 0;
    display: block;
  }
  .sofia-cal-fallback {
    padding: 40px 32px;
    font-family: var(--mono);
    font-size: 13px;
    color: var(--ink-3);
    line-height: 1.7;
    background: var(--bg-2);
  }
  .sofia-cal-fallback code {
    background: var(--bg);
    border: 1px solid var(--rule);
    padding: 2px 6px;
    font-size: 12px;
  }
  .sofia-faq-item {
    padding: 24px 0;
    border-top: 1px solid var(--rule);
  }
  .sofia-faq-item:last-child { border-bottom: 1px solid var(--rule); }
  .sofia-faq-item .q {
    font-family: var(--serif);
    font-size: 22px;
    color: var(--ink);
    margin-bottom: 10px;
  }
  .sofia-faq-item .a { font-size: 15px; color: var(--ink-2); line-height: 1.65; max-width: 820px; }
  .sofia-footer {
    border-top: 1px solid var(--rule);
    padding: 56px 0 40px;
    background: var(--bg-2);
  }
  .sofia-footer-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 28px;
  }
  .sofia-footer-links {
    display: flex;
    gap: 18px;
    font-size: 13px;
    color: var(--ink-2);
  }
  .sofia-footer-links a { color: var(--ink-2); text-decoration: none; }
  .sofia-footer-links a:hover { color: var(--ink); }
  .sofia-footer-compliance {
    margin-top: 32px;
    padding-top: 20px;
    border-top: 1px solid var(--rule);
    font-size: 12px;
    color: var(--ink-3);
    line-height: 1.6;
    max-width: 820px;
  }
  .sofia-footer-copyright {
    margin-top: 12px;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-3);
    letter-spacing: 0.06em;
  }

  @media (max-width: 900px) {
    .sofia-page section { padding: 64px 0; }
    .sofia-hero-grid { grid-template-columns: 1fr; gap: 32px; }
    .sofia-three-col { grid-template-columns: 1fr; gap: 16px; }
    .sofia-roi-grid { grid-template-columns: 1fr; gap: 16px; }
    .sofia-step-row { grid-template-columns: 1fr; gap: 8px; padding: 22px 0; }
    .sofia-cal-iframe { height: 720px; }
  }
  @media (max-width: 520px) {
    .sofia-page section { padding: 48px 0; }
    .sofia-hero-stats > div { padding: 16px 18px; }
    .sofia-hero-stats .value { font-size: 22px; }
    .sofia-stat-card { padding: 24px 20px; }
    .sofia-stat-card .num { font-size: 44px; }
    .sofia-roi-col { padding: 22px; }
  }
`;

/* ---------- COMPONENT ---------- */

function SofiaDemoPage() {
  const [iframeReady, setIframeReady] = useState(false);

  // TODO(operator): replace `{{CAL_SLUG}}` below with the real Cal.com slug
  // once Santiago creates the dedicated sofia-demo event in the sborzone
  // workspace. Suggested slug: sborzone/sofia-demo. See PLAN must_haves +
  // SUMMARY §operator-action.
  const calSlug = '{{CAL_SLUG}}';
  const calUrl = `https://cal.com/${calSlug}?overlayCalendar=true`;

  // Mirrors app.jsx's reveal-on-scroll bootstrap so this page animates in
  // consistently with the rest of the site (reuses the same .js-ready +
  // .is-revealed CSS hooks defined in landing/index.html).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    if (!('IntersectionObserver' in window)) {
      root.classList.add('js-ready');
      document.querySelectorAll('section').forEach((s) => s.classList.add('is-revealed'));
      return;
    }
    root.classList.add('js-ready');
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    const id = requestAnimationFrame(() => {
      document.querySelectorAll('section').forEach((s) => io.observe(s));
    });
    return () => {
      cancelAnimationFrame(id);
      io.disconnect();
    };
  }, []);

  const goLegal = (path) => (e) => { e.preventDefault(); navigateTo(path); };
  const goHome = (e) => { e.preventDefault(); navigateTo('/'); };

  return (
    <div className="sofia-page">
      <style>{STYLES}</style>

      {/* ---------- NAV ---------- */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'oklch(0.97 0.008 85 / 0.82)', backdropFilter: 'blur(10px)', borderBottom: '1px solid var(--rule)' }}>
        <div className="wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <a href="/" onClick={goHome} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.02em' }}>{COPY.nav.wordmark}</span>
          </a>
          <a href="#reserva" className="btn" style={{ padding: '10px 18px', textDecoration: 'none', background: 'var(--ink)', color: 'var(--bg)', border: '1px solid var(--ink)' }}>
            {COPY.nav.cta}
            <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: 6 }}><path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </a>
        </div>
      </div>

      {/* ---------- §01 HERO ---------- */}
      <section id="hero">
        <div className="wrap">
          <div className="sofia-hero-grid">
            <div>
              <div className="allcaps" style={{ color: 'var(--ink-3)', marginBottom: 24 }}>{COPY.hero.eyebrow}</div>
              <h1>
                <span>{COPY.hero.h1a}</span>
                <span className="h1b">{COPY.hero.h1b}</span>
              </h1>
              <p style={{ fontSize: 18, maxWidth: 560, marginBottom: 28 }}>{COPY.hero.sub}</p>
              <div className="sofia-cta-row">
                <a href="#reserva" className="btn" style={{ padding: '14px 22px', textDecoration: 'none', background: 'var(--ink)', color: 'var(--bg)', border: '1px solid var(--ink)' }}>{COPY.hero.cta}</a>
                <a href="#inversion" className="btn btn-ghost" style={{ padding: '14px 22px', textDecoration: 'none' }}>{COPY.hero.ctaSecondary}</a>
              </div>
            </div>
            <div className="sofia-hero-stats">
              {COPY.hero.stats.map((s) => (
                <div key={s.label}>
                  <div className="label">{s.label}</div>
                  <div className="value">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- §02 PROBLEMA ---------- */}
      <section id="problema">
        <div className="wrap">
          <div className="section-label">
            <span className="num">{COPY.problema.eyebrow.split(' · ')[0]}</span>
            <span className="name">{COPY.problema.eyebrow.split(' · ')[1]}</span>
            <span className="line" />
          </div>
          <h2 style={{ maxWidth: 920, marginBottom: 48 }}>{COPY.problema.h2}</h2>
          <div className="sofia-three-col">
            {COPY.problema.cards.map((c, i) => (
              <div key={i} className="sofia-stat-card">
                <div className="num">{c.n}</div>
                <div className="unit">{c.unit}</div>
                <div className="body">{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- §03 SOLUCIÓN ---------- */}
      <section id="solucion">
        <div className="wrap">
          <div className="section-label">
            <span className="num">{COPY.solucion.eyebrow.split(' · ')[0]}</span>
            <span className="name">{COPY.solucion.eyebrow.split(' · ')[1]}</span>
            <span className="line" />
          </div>
          <h2 style={{ maxWidth: 920, marginBottom: 48 }}>{COPY.solucion.h2}</h2>
          <div>
            {COPY.solucion.steps.map((s) => (
              <div key={s.n} className="sofia-step-row">
                <div className="n">{s.n}</div>
                <div>
                  <div className="title">{s.title}</div>
                  <div className="body">{s.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="sofia-stack-row">
            {COPY.solucion.stack.map((t) => (
              <span key={t} className="tag"><span className="dot" />{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- §04 ROI ---------- */}
      <section id="inversion">
        <div className="wrap">
          <div className="section-label">
            <span className="num">{COPY.roi.eyebrow.split(' · ')[0]}</span>
            <span className="name">{COPY.roi.eyebrow.split(' · ')[1]}</span>
            <span className="line" />
          </div>
          <h2 style={{ maxWidth: 920, marginBottom: 32 }}>{COPY.roi.h2}</h2>
          <div className="sofia-roi-grid">
            <div className="sofia-roi-col">
              <h3>{COPY.roi.leftTitle}</h3>
              <ul>
                {COPY.roi.leftItems.map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </div>
            <div className="sofia-roi-col">
              <h3>{COPY.roi.rightTitle}</h3>
              <ul>
                {COPY.roi.rightItems.map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </div>
          </div>
          <div className="sofia-roi-summary">
            <div className="big">{COPY.roi.summary}</div>
            <div className="calc">{COPY.roi.summaryCalc}</div>
          </div>
          <div className="sofia-kit-card">
            <div className="kit-title">{COPY.roi.kitTitle}</div>
            <div className="kit-body">{COPY.roi.kitBody}</div>
            <div className="kit-footnote">{COPY.roi.kitFootnote}</div>
          </div>
        </div>
      </section>

      {/* ---------- §05 RESERVA ---------- */}
      <section id="reserva">
        <div className="wrap">
          <div className="section-label">
            <span className="num">{COPY.reserva.eyebrow.split(' · ')[0]}</span>
            <span className="name">{COPY.reserva.eyebrow.split(' · ')[1]}</span>
            <span className="line" />
          </div>
          <h2 style={{ maxWidth: 920, marginBottom: 16 }}>{COPY.reserva.h2}</h2>
          <p style={{ fontSize: 17, maxWidth: 720, marginBottom: 0 }}>{COPY.reserva.sub}</p>

          {/*
            Cal.com embed — iframe pattern (matches diagnostico.jsx CalendarEmbed).
            The slug is a literal `{{CAL_SLUG}}` placeholder until the operator
            creates the dedicated 30-min sofia-demo event in the sborzone
            workspace. Until then this iframe will render Cal.com's "team or
            user not found" page — that's expected and flagged in the SUMMARY.
          */}
          <div className="sofia-cal-wrapper">
            <iframe
              src={calUrl}
              className="sofia-cal-iframe"
              title="Reserva una llamada de 20 minutos con Santiago"
              onLoad={() => setIframeReady(true)}
            />
            {!iframeReady && (
              <div className="sofia-cal-fallback" aria-hidden="true">
                Cargando calendario… Si no aparece, escribe a{' '}
                <a href="mailto:hola@enautomatico.pro" style={{ color: 'var(--on-ink)' }}>hola@enautomatico.pro</a>{' '}
                o usa <code>{calSlug}</code> directamente en cal.com.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ---------- §06 FAQ ---------- */}
      <section id="faq">
        <div className="wrap">
          <div className="section-label">
            <span className="num">{COPY.faq.eyebrow.split(' · ')[0]}</span>
            <span className="name">{COPY.faq.eyebrow.split(' · ')[1]}</span>
            <span className="line" />
          </div>
          <div style={{ maxWidth: 880 }}>
            {COPY.faq.items.map((it, i) => (
              <div key={i} className="sofia-faq-item">
                <div className="q">{it.q}</div>
                <div className="a">{it.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="sofia-footer">
        <div className="wrap">
          <div className="sofia-footer-row">
            <div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.02em', marginBottom: 6 }}>{COPY.nav.wordmark}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{COPY.footer.tagline}</div>
            </div>
            <div className="sofia-footer-links">
              <a href="/aviso-legal" onClick={goLegal('/aviso-legal')}>Aviso legal</a>
              <a href="/privacidad" onClick={goLegal('/privacidad')}>Privacidad</a>
              <a href="/cookies" onClick={goLegal('/cookies')}>Cookies</a>
              <a href="mailto:hola@enautomatico.pro">hola@enautomatico.pro</a>
            </div>
          </div>
          <div className="sofia-footer-compliance">{COPY.footer.compliance}</div>
          <div className="sofia-footer-copyright">{COPY.footer.copyright}</div>
        </div>
      </footer>
    </div>
  );
}

export { SofiaDemoPage };
